import React, {
  useRef,
  useEffect,
  useState,
  forwardRef,
  ReactNode,
  CSSProperties,
  HTMLAttributes,
} from 'react'
import {
  StyledLineCloseToMeElement,
  StyledWrapWithLineBetween,
} from './styles/WrapWithLineBetweenDisplayer.styles'

// Type definitions
interface LineCloseToMeElementProps extends HTMLAttributes<HTMLDivElement> {
  isLeft?: boolean
  isRight?: boolean
}

interface WrapWithLineBetweenProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode
  style?: CSSProperties
}

interface ChildPosition {
  isLeft: boolean
  isRight: boolean
}

interface ElementPosition {
  index: number
  x: number
  y: number
  width: number
  right: number
}

interface RowGroups {
  [key: number]: ElementPosition[]
}

export const LineCloseToMeElement = forwardRef<
  HTMLDivElement,
  LineCloseToMeElementProps
>(({ isLeft = true, isRight = true, ...props }, ref) => (
  <StyledLineCloseToMeElement
    ref={ref}
    isLeft={isLeft}
    isRight={isRight}
    {...props}
  />
))

LineCloseToMeElement.displayName = 'LineCloseToMeElement'

export const WrapWithLineBetween: React.FC<WrapWithLineBetweenProps> = ({
  children,
  style,
  ...props
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [childPositions, setChildPositions] = useState<ChildPosition[]>([])
  const childRefs = useRef<(HTMLElement | null)[]>([])

  useEffect(() => {
    // Reset refs array when children change
    const childCount = React.Children.count(children)

    childRefs.current = Array(childCount).fill(null)
  }, [children])

  useEffect(() => {
    const updateChildPositions = () => {
      if (!containerRef.current) return

      // Clean up any stale refs
      childRefs.current = childRefs.current.slice(
        0,
        React.Children.count(children),
      )

      // Get positions of all children
      const positions: ElementPosition[] = childRefs.current
        .map((ref, index) => {
          if (!ref || !ref.getBoundingClientRect) return null

          const rect = ref.getBoundingClientRect()

          return {
            index,
            x: rect.left,
            y: rect.top,
            width: rect.width,
            right: rect.right,
          }
        })
        .filter((pos): pos is ElementPosition => pos !== null)

      // Group by Y position (within 1px tolerance for float point differences)
      const rowGroups: RowGroups = positions.reduce((groups, pos) => {
        const row = Math.round(pos.y) // Round to handle minor float differences

        if (!groups[row]) groups[row] = []
        groups[row].push(pos)

        return groups
      }, {} as RowGroups)

      // For each row, determine leftmost and rightmost elements
      const newPositions: ChildPosition[] = positions.map(() => ({
        isLeft: true,
        isRight: true,
      }))

      Object.entries(rowGroups).forEach(([y, row]) => {
        // Sort by X position
        row.sort((a: ElementPosition, b: ElementPosition) => a.x - b.x)

        // Update border flags for this row
        row.forEach((pos: ElementPosition, idx: number) => {
          const isFirstInRow = idx === 0
          const isLastInRow = idx === row.length - 1

          newPositions[pos.index] = {
            isLeft: !isFirstInRow,
            isRight: !isLastInRow,
          }
        })
      })

      setChildPositions(newPositions)
    }

    const observer = new ResizeObserver(updateChildPositions)

    if (containerRef.current) {
      observer.observe(containerRef.current)
      // Initial update after a short delay to ensure refs are set
      setTimeout(updateChildPositions, 0)
    }

    return () => {
      observer.disconnect()
    }
  }, [children])

  return (
    <StyledWrapWithLineBetween ref={containerRef} style={style} {...props}>
      {React.Children.map(children, (child, index) => {
        if (!child || !React.isValidElement(child)) return child

        const borders = {
          isLeft: childPositions[index]?.isLeft ?? true,
          isRight: childPositions[index]?.isRight ?? true,
        }

        return React.cloneElement(child, {
          ...borders,
          key: index, // Ensure each child has a stable key
          ref: (el: HTMLElement | null) => {
            childRefs.current[index] = el
          },
        } as any)
      })}
    </StyledWrapWithLineBetween>
  )
}
